import { useEffect, useMemo, useState } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { fetchPreview } from "@/lib/api";
import { fr } from "@/lib/format";
import { Lock, ShieldCheck, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Step8LeadGate({ inputs, lead, setLead }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").split("-")[0];
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPreview(inputs)
      .then((d) => { if (alive) { setPreview(d); setErr(null); } })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [inputs]);

  const set = (patch) => setLead({ ...lead, ...patch });

  const radarData = useMemo(() => {
    if (!preview?.scores?.dimensions) return [];
    const labelsFr = {
      governance: "Gouvernance", hygiene: "Hygiène", privileges: "Privilèges",
      monitoring: "Surveillance", investigation: "Investigation",
      compliance: "Conformité", resilience: "Résilience",
    };
    const labelsEn = {
      governance: "Governance", hygiene: "Hygiene", privileges: "Privileges",
      monitoring: "Monitoring", investigation: "Investigation",
      compliance: "Compliance", resilience: "Resilience",
    };
    const labels = lang === "en" ? labelsEn : labelsFr;
    return Object.entries(preview.scores.dimensions).map(([k, v]) => ({ dim: labels[k], score: v }));
  }, [preview, lang]);

  const critColor = (c) => {
    const m = {
      critique: "text-red-700 bg-red-50 border-red-200",
      critical: "text-red-700 bg-red-50 border-red-200",
      élevée: "text-orange-700 bg-orange-50 border-orange-200",
      high: "text-orange-700 bg-orange-50 border-orange-200",
      moyenne: "text-amber-700 bg-amber-50 border-amber-200",
      medium: "text-amber-700 bg-amber-50 border-amber-200",
      modérée: "text-slate-700 bg-slate-50 border-slate-200",
      moderate: "text-slate-700 bg-slate-50 border-slate-200",
      faible: "text-slate-500 bg-slate-50 border-slate-200",
      low: "text-slate-500 bg-slate-50 border-slate-200",
    };
    return m[c] || "text-slate-500 bg-slate-50 border-slate-200";
  };

  return (
    <div className="py-8 space-y-8" data-testid="step-8">
      <div>
        <div className="eyebrow">{t("wizard.s8_ready")}</div>
        <h3 className="mt-2 text-2xl font-bold text-slate-900">{t("wizard.s8_preview")}</h3>
        <p className="mt-2 text-slate-600 leading-relaxed max-w-3xl">
          {t("wizard.s8_ask")}
        </p>
      </div>

      {loading && <div className="text-slate-500 text-sm">{t("wizard.s8_calc")}</div>}
      {err && <div className="text-sm text-destructive">{String(err)}</div>}

      {preview && (
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left column — teaser cards */}
          <div className="lg:col-span-3 space-y-6">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="kpi border-brand-fuchsia">
                <div className="label-caps">{t("wizard.s8_kpi_maturity")}</div>
                <div className="mt-1 text-3xl font-bold text-slate-900">{preview.scores.global}<span className="text-lg text-slate-400">/100</span></div>
                <div className="mt-1 text-sm text-brand-bordeaux font-semibold">{t(`results.levels.${preview.scores.level}`, { defaultValue: preview.scores.level })}</div>
                <div className="text-xs text-slate-500">{t("wizard.s8_kpi_confidence")} : {t(`results.confidence.${preview.scores.confidence}`, { defaultValue: preview.scores.confidence })}</div>
              </div>
              <div className="kpi">
                <div className="label-caps">{t("wizard.s8_kpi_risks")}</div>
                <div className="mt-1 text-3xl font-bold text-slate-900">{preview.risk_count}</div>
                <div className="mt-1 text-xs text-slate-500">{t("wizard.s8_reco")}</div>
                <div className="text-sm font-semibold text-slate-800 leading-snug">{preview.netwrix_package}</div>
              </div>
              <div className="kpi">
                <div className="label-caps">{t("wizard.s8_risk_avoided")}</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">
                  {preview.roi_teaser?.risk_avoided_annual ? fr.currency(preview.roi_teaser.risk_avoided_annual) : "—"}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {t("wizard.s8_disc_benefits")} : {preview.roi_teaser?.discounted_benefits ? fr.currency(preview.roi_teaser.discounted_benefits) : "—"}
                </div>
              </div>
            </div>

            {/* Top 3 risks */}
            <div className="card-flat p-5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-brand-fuchsia" />
                <div className="label-caps">{t("wizard.s8_top_risks")}</div>
              </div>
              <ul className="mt-4 space-y-3" data-testid="teaser-top-risks">
                {(preview.top_risks_teaser || []).map((r, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 h-6 w-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="flex-1">
                      <div className="text-slate-900 font-semibold leading-snug">{r.title}</div>
                      <span className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm border ${critColor(r.criticality)}`}>
                        {r.criticality}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Locked budget + ROI + PDF preview */}
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-5 relative overflow-hidden" data-testid="teaser-locked">
              <div className="label-caps">{t("wizard.s8_locked_title")}</div>
              <div className="mt-3 grid grid-cols-2 gap-4 filter blur-[3px] select-none">
                <div>
                  <div className="text-xs text-slate-500">{t("wizard.s8_locked_services")}</div>
                  <div className="text-xl font-bold text-slate-900">
                    {preview.budget_preview?.total_y1_low != null
                      ? `${fr.currency(preview.budget_preview.total_y1_low)} – ${fr.currency(preview.budget_preview.total_y1_high)}`
                      : "— €"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{t("wizard.s8_locked_roi")}</div>
                  <div className="text-xl font-bold text-slate-900">— %</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500 filter blur-[3px] select-none">{t("wizard.s8_locked_pdf")}</div>
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 bg-white px-4 py-2 rounded-full border border-slate-300 shadow-sm">
                  <Lock className="h-4 w-4 text-brand-fuchsia" /> {t("wizard.s8_locked_cta")}
                </div>
              </div>
            </div>
          </div>

          {/* Right column — radar + lead form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card-flat p-5">
              <div className="label-caps">{t("wizard.s8_radar")}</div>
              <div className="mt-3" style={{ height: 260 }} data-testid="teaser-radar">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: "#334155", fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 9 }} />
                    <Radar name="Score" dataKey="score" stroke="#DE0F72" fill="#DE0F72" fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card-flat p-6">
              <div className="eyebrow">{t("wizard.s8_form_eyebrow")}</div>
              <h3 className="mt-2 text-xl font-bold text-slate-900">{t("wizard.s8_form_title")}</h3>
              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-caps" htmlFor="lead-firstname">{t("wizard.s8_firstname")}</label>
                    <Input id="lead-firstname" value={lead.firstName} onChange={(e) => set({ firstName: e.target.value })} className="mt-2" data-testid="lead-firstname" />
                  </div>
                  <div>
                    <label className="label-caps" htmlFor="lead-lastname">{t("wizard.s8_lastname")}</label>
                    <Input id="lead-lastname" value={lead.lastName} onChange={(e) => set({ lastName: e.target.value })} className="mt-2" data-testid="lead-lastname" />
                  </div>
                </div>
                <div>
                  <label className="label-caps" htmlFor="lead-email">{t("wizard.s8_email")}</label>
                  <Input id="lead-email" type="email" value={lead.email} onChange={(e) => set({ email: e.target.value })} className="mt-2" data-testid="lead-email" />
                </div>
                <div>
                  <label className="label-caps" htmlFor="lead-company">{t("wizard.s8_company")}</label>
                  <Input id="lead-company" value={lead.company} onChange={(e) => set({ company: e.target.value })} className="mt-2" data-testid="lead-company" />
                </div>
              </div>
              <div className="mt-4 flex items-start gap-3">
                <Checkbox checked={!!lead.consent} onCheckedChange={(v) => set({ consent: !!v })} id="lead-consent" data-testid="lead-consent" />
                <label htmlFor="lead-consent" className="text-sm text-slate-600 leading-relaxed">
                  {t("wizard.s8_consent")}
                </label>
              </div>
              <p className="mt-4 text-xs text-slate-500 flex items-center gap-2">
                <ShieldCheck className="h-3 w-3 text-brand-fuchsia" /> {t("wizard.s8_privacy")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const step8Valid = (lead) => !!(lead.firstName && lead.lastName && /.+@.+\..+/.test(lead.email) && lead.company && lead.consent);
